import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import API from '../../api';
import { useLang, localeFor } from '../../LangContext';
import ProductCard from '../../components/bazaar/ProductCard';
import Pagination from '../../components/Pagination';
import { ETHIOPIAN_CITIES, CITY_TRANSLATIONS, categoryImage, CATEGORY_ICONS, PRODUCT_CATEGORIES, normalizeCategory } from '../../constants';
import { addToCart, getCartKey } from './cartUtils';
import { effectivePrice, priceRangeToBounds } from './filterUtils';

const ORANGE = '#FF6B00';
const DEFAULT_CATEGORY_ICON = { icon: '📦', color: '#F3F4F6' };

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      style={{ height: '100%' }}
    >
      {children}
    </motion.div>
  );
};

// ─── Category icon/color map (shared from constants) ───

export default function BazaarPage() {
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    seller_id: '',
    min_price: '',
    max_price: '',
    priceRange: '',
    search: '',
    city: '',
    inStockOnly: false,
    sellers: [],
    sortBy: 'default',
  });
  const [toast, setToast] = useState('');
  const [bazaarClosed, setBazaarClosed] = useState(false);
  const [schedule, setSchedule] = useState({ isOpen: null, nextOpen: null, nextClose: null });
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [viewMode, setViewMode] = useState('grid');
  const [trendingPeriod, setTrendingPeriod] = useState('week');
  const [page, setPage] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [managedCategories, setManagedCategories] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [dealToday, setDealToday] = useState([]);
  const [dealWeek, setDealWeek] = useState([]);
  const [nowTs, setNowTs] = useState(Date.now());
  const catNavRef = useRef(null);
  const PER_PAGE = 8;
  const { t, lang } = useLang();
  const loc = localeFor(lang);

  // ─── Deal countdown helpers (hooks MUST stay above early returns) ───
  const endOfTodayTs = useMemo(() => { const d = new Date(); d.setHours(24, 0, 0, 0); return d.getTime(); }, []);
  const endOfWeekTs = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  }, []);

  const loadSettings = useCallback(() => {
    API.get('/bazaar/settings').then(r => {
      const d = r.data || {};
      setSchedule({
        isOpen: d.is_open === '1',
        nextOpen: d.next_open || d.open_date || null,
        nextClose: d.next_close || d.close_date || null,
      });
      if (d.is_open === '1') setBazaarClosed(false);
      if (d.is_open === '0') setBazaarClosed(true);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const loadProducts = useCallback(({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    return API.get('/bazaar/products')
      .then(r => {
        if (r.data?.bazaar_closed) {
          setProducts([]);
          setBazaarClosed(true);
        } else {
          setProducts(Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.products) ? r.data.products : []));
          setBazaarClosed(false);
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  // 1-second ticker for deal countdowns
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Countdown to the next open (when closed) or next close (when open)
  useEffect(() => {
    if (schedule.isOpen === null) return;
    const targetStr = schedule.isOpen ? schedule.nextClose : schedule.nextOpen;
    if (!targetStr) return;
    const target = new Date(targetStr).getTime();
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        loadSettings();
        loadProducts({ silent: true });
        return;
      }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [schedule, loadSettings, loadProducts]);

  useEffect(() => {
    loadProducts();

    API.get('/bazaar/products/trending', { params: { period: 'week', limit: 6 } })
      .then(r => setTrending(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    API.get('/bazaar/products/best-deals', { params: { period: 'today', limit: 2 } })
      .then(r => setDealToday((Array.isArray(r.data) ? r.data : []).slice(0, 2)))
      .catch(() => {});

    API.get('/bazaar/products/best-deals', { params: { period: 'week', limit: 4 } })
      .then(r => setDealWeek((Array.isArray(r.data) ? r.data : []).slice(0, 4)))
      .catch(() => {});

    API.get('/bazaar/categories')
      .then(r => {
        if (Array.isArray(r.data)) {
          setManagedCategories(r.data.map(c => c.name));
          const map = {};
          r.data.forEach(c => { if (c.image) map[c.name] = c.image; });
          setCategoryImages(map);
        }
      })
      .catch(() => {});
  }, [loadProducts]);

  const categories = useMemo(() => {
    // Exactly the canonical 8 — a 9th card never appears.
    const set = new Set(PRODUCT_CATEGORIES);
    managedCategories.forEach(n => set.add(normalizeCategory(n)));
    products.forEach(p => set.add(normalizeCategory(p.category || '')));
    return PRODUCT_CATEGORIES.filter(c => set.has(c));
  }, [products, managedCategories]);

  const sellers = useMemo(() => {
    const seen = new Set();
    return products
      .filter(p => p.seller_id && !seen.has(p.seller_id) && seen.add(p.seller_id))
      .map(p => ({ id: p.seller_id, name: p.seller_name }));
  }, [products]);

  const cities = useMemo(() => {
    const seen = new Set();
    products.forEach(p => {
      const loc = (p.seller_location || '').trim();
      if (loc) seen.add(loc);
    });
    return Array.from(seen).sort();
  }, [products]);

  const allCities = useMemo(() => {
    const seen = new Set([...ETHIOPIAN_CITIES, ...cities]);
    return Array.from(seen).sort();
  }, [cities]);

  const filtered = useMemo(() => {
    let result = products.filter(p => {
      if (filters.category && normalizeCategory(p.category || '') !== filters.category) return false;
      if (filters.seller_id && String(p.seller_id) !== String(filters.seller_id)) return false;
      if (filters.sellers?.length && !filters.sellers.some(s => String(s) === String(p.seller_id))) return false;
      if (filters.city && (p.seller_location || '').toLowerCase() !== filters.city.toLowerCase()) return false;
      if (filters.min_price !== '' && effectivePrice(p) < Number(filters.min_price)) return false;
      if (filters.max_price !== '' && effectivePrice(p) > Number(filters.max_price)) return false;
      if (filters.inStockOnly && Number(p.stock_quantity) === 0) return false;
      if (filters.search) {
        const term = filters.search.toLowerCase();
        if (!(p.name || '').toLowerCase().includes(term) && !(p.description || '').toLowerCase().includes(term)) return false;
      }
      return true;
    });

    if (filters.sortBy === 'price_asc') result = [...result].sort((a, b) => effectivePrice(a) - effectivePrice(b));
    else if (filters.sortBy === 'price_desc') result = [...result].sort((a, b) => effectivePrice(b) - effectivePrice(a));
    else if (filters.sortBy === 'name_asc') result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || '', loc));
    else if (filters.sortBy === 'newest') result = [...result].sort((a, b) => (b.id || 0) - (a.id || 0));

    return result;
  }, [products, filters]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const handleAddToCart = (product) => {
    const key = getCartKey(product.seller_id);
    if (!key) {
      // getCartKey returns null when there is no signed-in user profile. Say so
      // instead of silently dropping the click.
      showToast(t.signInToAdd);
      return false;
    }
    let cart = [];
    try { cart = JSON.parse(localStorage.getItem(key)) || []; } catch { cart = []; }
    if (!Array.isArray(cart)) cart = [];
    cart = addToCart(cart, product, 1);
    localStorage.setItem(key, JSON.stringify(cart));
    showToast(t.addedToWishlist(product.name));
    return true;
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100, color: 'var(--primary)', fontWeight: 700 }}>{t.loading}</div>;
  }

  if (bazaarClosed) {
    const pad = n => String(n).padStart(2, '0');
    const nextOpenStr = schedule.nextOpen;
    return (
      <div className="page" style={{ paddingTop: 100, textAlign: 'center' }}>
        <Reveal>
          <div style={{ fontSize: 72, marginBottom: 24 }}>🛍️</div>
          <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 16 }}>
            {t.bazaarTitle} <span style={{ color: 'var(--primary)' }}>{t.comingSoon}</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 48, maxWidth: 600, margin: '0 auto 48px' }}>
            {t.bazaarClosedDesc}
          </p>
          {nextOpenStr && (
            <div style={{ marginBottom: 48 }}>
              <div style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 17, marginBottom: 24, textTransform: 'uppercase', letterSpacing: '0.1em' }}>⏳ {t.opensIn}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                {[[t.days, countdown.days], [t.hours, countdown.hours], [t.minutes, countdown.minutes], [t.seconds, countdown.seconds]].map(([label, val]) => (
                  <div key={label} className="creative-card" style={{ padding: '24px 32px', minWidth: 100, textAlign: 'center' }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>{pad(val)}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 17, marginTop: 8, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="creative-card" style={{ display: 'inline-block', padding: '24px 40px' }}>
            <div style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 17, marginBottom: 8, textTransform: 'uppercase' }}>📅 {t.nextBazaar}</div>
            <div style={{ color: 'var(--secondary)', fontSize: 20, fontWeight: 900 }}>
              {nextOpenStr ? new Date(nextOpenStr).toLocaleDateString(loc, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : t.scheduledByAdmin}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 16, marginTop: 4 }}>{t.reopensAt(nextOpenStr ? new Date(nextOpenStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—')}</div>
          </div>
        </Reveal>
      </div>
    );
  }

  const activeCatKey = filters.category ? filters.category.toLowerCase() : '';
  const activeCatInfo = activeCatKey ? (CATEGORY_ICONS[activeCatKey] || DEFAULT_CATEGORY_ICON) : null;

  const fmtRemaining = (target) => {
    const diff = Math.max(0, target - nowTs);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  const DealCountdownChip = ({ target }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#ef44440F', border: '1px solid #ef444430', color: '#ef4444', fontWeight: 800, fontSize: 17, padding: '5px 12px', borderRadius: 100 }}>
      ⏳ {t.endsIn} <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtRemaining(target)}</span>
    </span>
  );

  const scrollCatNav = (dir) => {
    const el = catNavRef.current;
    if (el) el.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  const clearAllFilters = () => setFilters({
    category: '', seller_id: '', min_price: '', max_price: '', priceRange: '', search: '',
    city: '', inStockOnly: false, sellers: [], sortBy: 'default',
  });

  return (
    <div style={{ paddingTop: 0, background: 'var(--bg-main)', minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* ═══════════════════ HERO (1920×280) ═══════════════════ */}
      <section className="bazaar-hero">
        <img
          src="/images/bazzar.png"
          alt="Bazaar"
          onLoad={() => setImgLoaded(true)}
          style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center center',
            opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.6s ease',
          }}
        />
        <div className="bazaar-hero-overlay" style={{
          position: 'absolute', inset: 0,
        }} />
        <div className="bazaar-hero-text">
          <div className="bazaar-hero-text-inner">
            <Reveal>
              <h1 style={{ fontSize: 'clamp(24px, 3.5vw, 34px)', fontWeight: 900, lineHeight: 1.1, color: 'var(--secondary)', marginBottom: 10, margin: 0 }}>
                {t.bazaarTitle} <span style={{ color: '#F58220' }}>{t.bazaarMarketplaceWord}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p style={{ fontSize: 16, color: 'var(--text-muted)', lineHeight: 1.6, margin: '10px 0 20px' }}>
                {t.bazaarSubtitle}
              </p>
            </Reveal>

            {/* ─── Countdown in hero ─── */}
            {schedule.isOpen === true && schedule.nextClose && (
              <Reveal delay={0.3}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                  borderRadius: 10, padding: '10px 16px',
                }}>
                  <span style={{ fontSize: 16 }}>🟢</span>
                  <span style={{ fontWeight: 800, color: '#16a34a', fontSize: 17 }}>{t.openNow}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>{t.closesIn}</span>
                  <span style={{ fontWeight: 900, color: '#16a34a', fontSize: 17 }}>{countdown.days}d {countdown.hours}h {countdown.minutes}m</span>
                </div>
              </Reveal>
            )}
            {schedule.isOpen === false && schedule.nextOpen && (
              <Reveal delay={0.3}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: 10, padding: '10px 16px',
                }}>
                  <span style={{ fontSize: 16 }}>🔴</span>
                  <span style={{ fontWeight: 800, color: '#ef4444', fontSize: 17 }}>{t.closedNow}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>· Opens in</span>
                  <span style={{ fontWeight: 900, color: '#ef4444', fontSize: 17 }}>{countdown.days}d {countdown.hours}h {countdown.minutes}m</span>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════ BODY ═══════════════════ */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 24px 56px' }} className="bazaar-body">
        {/* ─── Breadcrumb ─── */}
        <nav style={{ fontSize: 16, color: 'var(--text-light)', marginBottom: 16 }}>
          <Link to="/" style={{ color: 'var(--text-light)', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = ORANGE} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-light)'}>{t.home}</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: ORANGE, fontWeight: 700 }}>{t.bazaarTitle}</span>
        </nav>



        {/* ─── Header: Title + Stat cards ─── */}
        <div className="bazaar-header">
          <div>
            <h1 style={{ fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 900, color: 'var(--secondary)', margin: '0 0 6px' }}>
              Bazaar
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 16, margin: 0 }}>
              Shop from verified Ethiopian sellers — handcrafted goods, fashion, food & more.
            </p>
          </div>
          <div className="bazaar-stat-grid">
            <StatCard value={products.length} label={t.statProducts} icon="🛍️" gradient="linear-gradient(135deg,#FF6B00,#FFA94D)" />
            <StatCard value={sellers.length} label={t.sellers} icon="🏪" gradient="linear-gradient(135deg,#6366F1,#A5B4FC)" />
            <StatCard value={categories.length} label={t.statCategories} icon="🏷️" gradient="linear-gradient(135deg,#10B981,#6EE7B7)" />
            <StatCard value={cities.length} label={t.statCities} icon="📍" gradient="linear-gradient(135deg,#F59E0B,#FCD34D)" />
          </div>
        </div>

        {/* ─── ⚡ Best Deal Today ─── */}
        {dealToday.length > 0 && (
          <Reveal>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              <h2 style={{ fontSize: 20, fontWeight: 900, color: 'var(--secondary)', margin: 0 }}>
                ⚡ {t.bestDealToday}
              </h2>
              <DealCountdownChip target={endOfTodayTs} />
            </div>
            <div className="bazaar-deals-today" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20, marginBottom: 28 }}>
              {dealToday.map((dealToday, idx) => (
                <Reveal key={dealToday.id} delay={Math.min(idx * 0.05, 0.25)}>
                  <Link to={`/bazaar/product/${dealToday.id}`} style={{ textDecoration: 'none', display: 'block' }}>
                    <div style={{
                      display: 'flex', gap: 16, alignItems: 'stretch', flexWrap: 'wrap', height: '100%',
                      background: 'var(--bg-main)', borderRadius: 16, border: '2px solid #FFE4C7',
                      boxShadow: '0 4px 16px rgba(255,107,0,0.06)', overflow: 'hidden', padding: 14,
                    }}>
                      <div style={{ width: 150, minWidth: 120, flexShrink: 0, borderRadius: 10, overflow: 'hidden', background: '#F3F4F6' }}>
                        {dealToday.photos?.[0]?.url ? (
                          <img src={dealToday.photos[0].url} alt={dealToday.name} style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                        ) : (
                          <div style={{ width: '100%', height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, color: '#D1D5DB' }}>📦</div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {dealToday.discount_percent != null && (
                            <span style={{ background: '#ef4444', color: '#fff', fontSize: 17, fontWeight: 900, padding: '3px 10px', borderRadius: 100 }}>
                              -{dealToday.discount_percent}% OFF
                            </span>
                          )}
                          <span style={{ background: '#FF6B000F', color: ORANGE, fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 100 }}>
                            🏪 {dealToday.seller_name}
                          </span>
                        </div>
                        <h3 style={{ fontSize: 17, fontWeight: 900, color: 'var(--secondary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dealToday.name}</h3>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ textDecoration: 'line-through', color: 'var(--text-light)', fontSize: 16, fontWeight: 600 }}>
                            ETB {Number(dealToday.price).toLocaleString()}
                          </span>
                          <span style={{ color: ORANGE, fontWeight: 900, fontSize: 20 }}>
                            ETB {Number(dealToday.discount_price).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 17, fontWeight: 700 }}>
                          You save ETB {(Number(dealToday.price) - Number(dealToday.discount_price)).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </Reveal>
        )}

        {/* ─── 🏆 Best Deals This Week ─── */}
        {dealWeek.length > 0 && (
          <Reveal>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              <h2 style={{ fontSize: 20, fontWeight: 900, color: 'var(--secondary)', margin: 0 }}>
                🏆 {t.bestDealsWeek}
              </h2>
              <DealCountdownChip target={endOfWeekTs} />
            </div>
            <div className="bazaar-deals-week" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 20, marginBottom: 28 }}>
              {dealWeek.map((product, idx) => (
                <Reveal key={product.id} delay={Math.min(idx * 0.05, 0.25)}>
                  <ProductCard product={product} onAddToCart={handleAddToCart} />
                </Reveal>
              ))}
            </div>
          </Reveal>
        )}

        {/* ─── Category nav (horizontal scroll with arrows + visible scrollbar) ─── */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <button aria-label={t.scrollCatLeft} onClick={() => scrollCatNav(-1)}
            style={{
              position: 'absolute', left: -6, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
              width: 34, height: 34, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.08)',
              background: 'var(--bg-main)', color: 'var(--secondary)', fontSize: 18, fontWeight: 900, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = ORANGE; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-main)'; e.currentTarget.style.color = 'var(--secondary)'; }}
          >‹</button>
          <button aria-label={t.scrollCatRight} onClick={() => scrollCatNav(1)}
            style={{
              position: 'absolute', right: -6, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
              width: 34, height: 34, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.08)',
              background: 'var(--bg-main)', color: 'var(--secondary)', fontSize: 18, fontWeight: 900, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = ORANGE; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-main)'; e.currentTarget.style.color = 'var(--secondary)'; }}
          >›</button>
          <div ref={catNavRef} className="cat-nav-scroll"
            style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 10, scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}>
            <CatNavItem label={t.allCategories} icon="✨" color="#F3F4F6" active={!filters.category} onClick={() => { setFilters(f => ({ ...f, category: '' })); setPage(1); }} />
            {categories.map(c => {
              const info = CATEGORY_ICONS[c.toLowerCase()] || DEFAULT_CATEGORY_ICON;
              return (
                <CatNavItem key={c} label={c} icon={info.icon} color={info.color} img={categoryImages[c] || categoryImage(c)}
                  active={filters.category === c}
                  onClick={() => { setFilters(f => ({ ...f, category: f.category === c ? '' : c })); setPage(1); }} />
              );
            })}
          </div>
          <style>{`
            .cat-nav-scroll { scrollbar-width: thin; scrollbar-color: ${ORANGE} rgba(0,0,0,0.06); }
            .cat-nav-scroll::-webkit-scrollbar { height: 6px; }
            .cat-nav-scroll::-webkit-scrollbar-track { background: rgba(0,0,0,0.05); border-radius: 3px; }
            .cat-nav-scroll::-webkit-scrollbar-thumb { background: ${ORANGE}; border-radius: 3px; }
          `}</style>
        </div>

        {/* ─── Two-column layout ─── */}
        <div className="bazaar-layout">
          {/* Filter sidebar */}
          <aside className="bazaar-aside">
            <div style={{
              background: 'var(--bg-main)', borderRadius: 16, border: '1px solid rgba(0,0,0,0.08)', padding: '20px 16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ fontSize: 17, fontWeight: 900, color: 'var(--secondary)', margin: 0 }}>{t.filters}</h3>
                <button onClick={clearAllFilters} style={{ background: 'none', border: 'none', color: 'var(--text-light)', fontSize: 17, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>{t.clearAll}</button>
              </div>

              {/* Search */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--secondary)', marginBottom: 10 }}>{t.searchLabel}</div>
                <div style={{ position: 'relative' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                  </svg>
                  <input
                    placeholder={t.searchProducts}
                    value={filters.search}
                    onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                    style={{ ...barInput, padding: '8px 12px 8px 36px', width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Category */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--secondary)', marginBottom: 10 }}>{t.category}</div>
                <select value={filters.category}
                  onChange={e => { setFilters(f => ({ ...f, category: e.target.value })); setPage(1); }}
                  style={{ ...barInput, width: '100%' }}>
                  <option value="">{t.allCategories}</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Price range */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--secondary)', marginBottom: 10 }}>{t.priceRangeEtb}</div>
                <select value={filters.priceRange}
                  onChange={e => {
                    const { min_price, max_price } = priceRangeToBounds(e.target.value);
                    setFilters(f => ({ ...f, priceRange: e.target.value, min_price, max_price }));
                    setPage(1);
                  }}
                  style={{ ...barInput, width: '100%' }}>
                  <option value="">{t.anyPrice}</option>
                  <option value="under-500">{t.priceUnder('500')}</option>
                  <option value="500-1000">{t.priceBand('500', '1,000')}</option>
                  <option value="1000-5000">{t.priceBand('1,000', '5,000')}</option>
                  <option value="5000-20000">{t.priceBand('5,000', '20,000')}</option>
                  <option value="over-20000">{t.priceOver('20,000')}</option>
                </select>
              </div>

              {/* Availability */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--secondary)', marginBottom: 10 }}>{t.availability}</div>
                <label style={checkLabel}>
                  <input type="checkbox" checked={filters.inStockOnly}
                    onChange={e => setFilters(f => ({ ...f, inStockOnly: e.target.checked }))} style={checkbox} />
                  In Stock Only
                </label>
              </div>

              {/* City dropdown */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--secondary)', marginBottom: 10 }}>{t.location}</div>
                <select value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} style={{ ...barInput, width: '100%' }}>
                  <option value="">{t.allLocations}</option>
                  {allCities.map(c => (
                    <option key={c} value={c}>{CITY_TRANSLATIONS[lang]?.[c] || c}</option>
                  ))}
                </select>
              </div>

              {/* Sellers */}
              {sellers.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--secondary)', marginBottom: 10 }}>{t.sellers}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 160, overflowY: 'auto' }}>
                    {sellers.map(s => (
                      <label key={s.id} style={checkLabel}>
                        <input type="checkbox"
                          checked={(filters.sellers || []).some(x => String(x) === String(s.id))}
                          onChange={e => {
                            const cur = filters.sellers || [];
                            setFilters(f => ({
                              ...f,
                              sellers: e.target.checked ? [...cur, s.id] : cur.filter(x => String(x) !== String(s.id)),
                            }));
                          }}
                          style={checkbox} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Apply Filters */}
              <button onClick={() => setPage(1)}
                style={{
                  width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                  background: ORANGE, color: '#fff', fontWeight: 800, fontSize: 17,
                  cursor: 'pointer', boxShadow: '0 6px 16px rgba(255,107,0,0.2)',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.08)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
              >{t.applyFilters}</button>
            </div>
          </aside>

          {/* Product area */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(127,127,127,0.35)',
            }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 17, fontWeight: 700 }}>
                {filtered.length === 0
                  ? t.resultsNone
                  : filtered.length === products.length
                    ? t.resultsCount(filtered.length)
                    : t.resultsCountOf(filtered.length, products.length)}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <select value={filters.sortBy}
                  onChange={e => { setFilters(f => ({ ...f, sortBy: e.target.value })); setPage(1); }}
                  style={selectStyle}>
                  <option value="default">{t.sortDefault}</option>
                  <option value="newest">{t.sortNewest}</option>
                  <option value="price_asc">{t.sortPriceAsc}</option>
                  <option value="price_desc">{t.sortPriceDesc}</option>
                  <option value="name_asc">{t.sortNameAsc}</option>
                </select>
                <div style={{ display: 'flex', background: 'var(--bg-subtle)', borderRadius: 10, padding: 4, border: '1px solid rgba(127,127,127,0.35)' }}>
                  <button onClick={() => setViewMode('grid')} aria-label={t.gridView}
                    style={{ ...viewBtnStyle, background: viewMode === 'grid' ? 'var(--bg-main)' : 'transparent', boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                  </button>
                  <button onClick={() => setViewMode('list')} aria-label={t.listView}
                    style={{ ...viewBtnStyle, background: viewMode === 'list' ? 'var(--bg-main)' : 'transparent', boxShadow: viewMode === 'list' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                <div style={{ fontSize: 64, marginBottom: 24 }}>🔍</div>
                <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t.noResults}</p>
                <button onClick={clearAllFilters} style={{ color: ORANGE, background: 'none', border: 'none', fontSize: 17, fontWeight: 800, cursor: 'pointer' }}>
                  {t.clearAll}
                </button>
              </div>
            ) : (
              <>
                <div className={viewMode === 'grid' ? 'bazaar-grid' : ''} style={viewMode === 'list' ? listStyle : {}}>
                  {filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((product, idx) => (
                    <Reveal key={product.id} delay={Math.min(idx * 0.03, 0.3)}>
                      <ProductCard product={product} onAddToCart={handleAddToCart} viewMode={viewMode} />
                    </Reveal>
                  ))}
                </div>
                <Pagination currentPage={page} totalPages={Math.ceil(filtered.length / PER_PAGE)} onPageChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
              </>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={toastStyle} role="status">
          {toast}
        </motion.div>
      )}

      <style>{`
        .bazaar-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px; }
        :root.dark .cat-nav-scroll button > span, [data-theme="dark"] .cat-nav-scroll button > span {
          background: #1E293B !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
        }
        .bazaar-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 16px; margin-bottom: 24px; }
        .bazaar-stat-grid { display: grid; grid-template-columns: repeat(4, auto); gap: 12px; }
        .bazaar-layout { display: flex; gap: 24px; align-items: flex-start; }
        .bazaar-aside { flex: 0 0 200px; width: 200px; position: sticky; top: 90px; }

        @media (max-width: 1024px) {
          .bazaar-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 900px) {
          .bazaar-layout { flex-direction: column !important; }
          .bazaar-aside { flex: 1 1 100% !important; width: 100% !important; position: relative !important; top: 0 !important; }
          .bazaar-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .bazaar-deals-week { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .bazaar-deals-today { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 640px) {
          .bazaar-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .bazaar-header { flex-direction: column !important; align-items: flex-start !important; }
          .bazaar-deals-today { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 480px) {
          .bazaar-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
          .bazaar-deals-week { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
          .bazaar-stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .bazaar-aside { width: 100% !important; }
          .bazaar-body { padding: 20px 12px 60px !important; }
          .bazaar-grid .jiji-card > div:first-child { height: 150px !important; }
          .bazaar-grid .jiji-card > div:not(:first-child) { padding: 12px !important; gap: 8px !important; }
          .bazaar-grid .jiji-card h3 { font-size: 14px !important; }
        }
      `}</style>
    </div>
  );
}

/* ─── Stat Card ─── */
const StatCard = ({ value, label, icon, gradient }) => (
  <div style={{
    background: 'var(--bg-main)', borderRadius: 14, border: '1px solid rgba(0,0,0,0.08)', padding: '14px 18px',
    display: 'flex', alignItems: 'center', gap: 12, minWidth: 110,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)', transition: 'all 0.3s', cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.03)'; }}
  >
    <div style={{
      width: 40, height: 40, borderRadius: 12, background: gradient, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--secondary)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 17, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  </div>
);

/* ─── Category nav item ─── */
const CatNavItem = ({ label, icon, color, img, active, onClick }) => (
  <button onClick={onClick} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0,
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px', fontFamily: 'inherit',
  }}>
    {img ? (
      <span style={{
        width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', display: 'inline-flex',
        transition: 'all 0.3s', boxShadow: active ? `0 0 0 3px ${ORANGE}30` : '0 2px 8px rgba(0,0,0,0.05)',
        transform: active ? 'scale(1.05)' : 'scale(1)',
      }}>
        <img src={img} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </span>
    ) : (
      <span style={{
        width: 52, height: 52, borderRadius: '50%', background: color, fontSize: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s', boxShadow: active ? `0 0 0 3px ${ORANGE}30` : '0 2px 8px rgba(0,0,0,0.05)',
        transform: active ? 'scale(1.05)' : 'scale(1)',
      }}>{icon}</span>
    )}
    <span style={{
      fontSize: 17, fontWeight: 700, color: active ? ORANGE : 'var(--text-muted)', whiteSpace: 'nowrap',
      borderBottom: active ? `2px solid ${ORANGE}` : '2px solid transparent',
      paddingBottom: 2, transition: 'all 0.3s',
    }}>{label}</span>
  </button>
);

const barInput = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid rgba(0,0,0,0.08)',
  background: 'var(--bg-subtle)',
  color: 'var(--secondary)',
  fontSize: 16,
  fontWeight: 600,
  outline: 'none',
  fontFamily: 'inherit',
  minWidth: 120,
};

const checkLabel = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 16, fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer',
};

const checkbox = { accentColor: ORANGE, width: 16, height: 16, cursor: 'pointer' };

const selectStyle = {
  padding: '10px 16px',
  borderRadius: 10,
  background: 'var(--bg-main)',
  border: '1px solid rgba(0,0,0,0.08)',
  color: 'var(--secondary)',
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  outline: 'none',
  fontFamily: 'inherit',
};

const viewBtnStyle = {
  width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 18,
  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', color: 'var(--secondary)',
};

const listStyle = {
  display: 'flex', flexDirection: 'column', gap: 20,
};

const toastStyle = {
  position: 'fixed', bottom: 40, right: 40, background: 'rgba(15,23,42,0.95)', color: '#fff',
  padding: '16px 32px', borderRadius: 20, fontWeight: 800, fontSize: 17, zIndex: 9999,
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
};
